import type { FullScreenEmailEditorStep } from "./FullScreenEmailEditorContext";

export type ExitAction = "save-and-exit" | "back" | "logo";

export interface ExitBehavior {
  shouldSave: boolean;
  shouldDeleteDraft: boolean;
  shouldShowModal: boolean;
  navigateTo: string | "previous-step";
}

interface ExitBehaviorContext {
  firstDirty: boolean;
  isNewEmail: boolean;
  currentStep: FullScreenEmailEditorStep;
  isCreationFlow: boolean;
  templateStepSkipped: boolean;
}

export function getExitBehavior(action: ExitAction, targetUrl: string, context: ExitBehaviorContext): ExitBehavior {
  const { firstDirty, isNewEmail, currentStep, isCreationFlow, templateStepSkipped } = context;

  const goToPreviousStep: ExitBehavior = {
    shouldSave: false,
    shouldDeleteDraft: false,
    shouldShowModal: false,
    navigateTo: "previous-step",
  };
  const goToTarget: ExitBehavior = {
    shouldSave: false,
    shouldDeleteDraft: false,
    shouldShowModal: false,
    navigateTo: targetUrl,
  };
  const showModal: ExitBehavior = {
    shouldSave: false,
    shouldDeleteDraft: false,
    shouldShowModal: true,
    navigateTo: targetUrl,
  };

  // ── Template step ──
  if (currentStep === "template") {
    return { ...goToTarget, shouldDeleteDraft: isCreationFlow && !firstDirty && isNewEmail };
  }

  // ── Body step ──
  if (currentStep === "body") {
    // back action
    if (action === "back") {
      // new emails with changes should show modal on back
      if (firstDirty && isNewEmail) {
        return showModal;
      }
      if (templateStepSkipped) {
        return { ...goToTarget, shouldSave: firstDirty };
      }
      return { ...goToPreviousStep, shouldSave: firstDirty };
    }
    // always save on save-and-exit except for new emails without changes
    if (action === "save-and-exit") {
      if (!firstDirty && isNewEmail) {
        return { ...goToTarget, shouldSave: false, shouldDeleteDraft: true };
      }
      return { ...goToTarget, shouldSave: true };
    }
    // logo exit
    if (!firstDirty) {
      return { ...goToTarget, shouldDeleteDraft: isNewEmail };
    }
    if (!isNewEmail) {
      return { ...goToTarget, shouldSave: true };
    }
    // new emails with changes should show modal on logo exit
    return showModal;
  }

  // ── Preferences / Recipients steps ──
  if (currentStep === "preferences" || currentStep === "recipients") {
    if (action === "back") {
      return goToPreviousStep;
    }
    return { ...goToTarget, shouldSave: firstDirty };
  }

  // ── Delivery step ──
  if (action === "back") {
    return goToPreviousStep;
  }
  return goToTarget;
}
